import { Check, Pause, Plus, Search, Sparkles, Trash2, Upload } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { Source } from '../types'
import { Card, EmptyState, StatusBadge } from '../components/UI'
import { PageHeader } from '../components/Layout'

type Props = {
  initialSources: Source[]
  onPropose?: (source: Source) => Promise<void>
  onStatus?: (id: string, status: Source['status']) => Promise<void>
}

export function SourcesPage({ initialSources, onPropose, onStatus }: Props) {
  const [sources, setSources] = useState(initialSources)
  const [query, setQuery] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')
  const filtered = useMemo(
    () => sources.filter((source) => source.username.includes(query.toLowerCase()) || source.panel.toLowerCase().includes(query.toLowerCase())),
    [query, sources],
  )

  const add = async () => {
    const username = url.split('/').filter(Boolean).at(-1)?.replace('@', '') ?? ''
    if (!username) return
    const source: Source = {
      id: crypto.randomUUID(), username, platform: 'Instagram', panel: 'Unclassified', status: 'proposed',
      leadTimeHours: null, usefulDiscoveries: 0, repostRatio: 0, costUsd: 0,
    }
    try {
      if (onPropose) await onPropose(source)
      setSources([...sources, source]); setUrl(''); setShowAdd(false); setError('')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Source could not be proposed.') }
  }

  const changeStatus = async (source: Source, status: Source['status']) => {
    try {
      if (onStatus) await onStatus(source.id, status)
      setSources(sources.map((item) => item.id === source.id ? { ...item, status } : item)); setError('')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Source status could not be changed.') }
  }

  return <>
    <PageHeader eyebrow="Collection intelligence" title="Sources" body="Monitor a balanced panel, learn who discovers useful signals early, and approve every suggested account." actions={<><button className="button ghost"><Upload /> Bulk paste</button><button className="button primary" onClick={() => setShowAdd(true)}><Plus /> Add source</button></>} />
    {showAdd && <Card className="inline-form"><div><h2>Add an Instagram source</h2><p>Paste a public profile URL. It remains proposed until you approve it.</p></div><input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://instagram.com/username/" /><button className="button primary" onClick={add}>Test and propose</button><button className="icon-button" onClick={() => setShowAdd(false)}>×</button></Card>}
    {error && <div className="notice warning"><div><strong>Nothing was changed</strong><span>{error}</span></div></div>}
    <div className="source-summary"><Card><strong>{sources.filter((source) => source.status === 'active').length}</strong><span>active sources</span></Card><Card><strong>{sources.filter((source) => source.status === 'proposed').length}</strong><span>awaiting approval</span></Card><Card><strong>${sources.reduce((sum, source) => sum + source.costUsd, 0).toFixed(2)}</strong><span>estimated this month</span></Card><Card><strong>{new Set(sources.map((source) => source.panel)).size}</strong><span>source panels</span></Card></div>
    <Card>
      <div className="table-toolbar"><div className="search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search accounts or panels" /></div><span>{filtered.length} results</span></div>
      <div className="data-table source-table">
        <div className="table-head"><span>Account</span><span>Panel</span><span>Status</span><span>Lead time</span><span>Useful signals</span><span>Reposts</span><span>Actions</span></div>
        {filtered.map((source) => <div className="table-row" key={source.id}>
          <span><strong>@{source.username}</strong><small>{source.platform}</small></span><span>{source.panel}</span><span><StatusBadge status={source.status} /></span>
          <span>{source.leadTimeHours == null ? 'Warming up' : `${source.leadTimeHours}h`}</span><span>{source.usefulDiscoveries}</span><span>{Math.round(source.repostRatio * 100)}%</span>
          <span className="row-actions">
            {source.status === 'proposed' ? <button title="Approve" onClick={() => changeStatus(source, 'active')}><Check /></button> : <button title="Pause" onClick={() => changeStatus(source, 'paused')}><Pause /></button>}
            <button title="Remove" onClick={() => changeStatus(source, 'paused')}><Trash2 /></button>
          </span>
        </div>)}
      </div>
      {!filtered.length && <EmptyState title="No sources match" body="Clear the search or propose a new public account." />}
    </Card>
    <div className="notice"><Sparkles /><div><strong>Suggested sources never activate themselves</strong><span>The system shows why an account may help, expected cost, overlap, and evidence before you approve it.</span></div></div>
  </>
}
